import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

const GA_ID = 'G-3DX62F3FYP';

function updateGoogleConsent(granted) {
  if (typeof window === 'undefined') return;
  window[`ga-disable-${GA_ID}`] = !granted;
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: granted ? 'granted' : 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
  }
}

function enableAnalytics() {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };
  updateGoogleConsent(true);

  if (!document.querySelector('script[data-nexus-analytics]')) {
    const script = document.createElement('script');
    script.async = true;
    script.dataset.nexusAnalytics = 'consented';
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    document.head.appendChild(script);
  }

  window.gtag('js', new Date());
  window.gtag('config', GA_ID, { anonymize_ip: true });
}

function disableAnalytics() {
  updateGoogleConsent(false);
}

export default function CookieConsent() {
  const { language } = useLanguage();
  const isTurkish = language === 'tr';
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });
  const settingsBtnRef = useRef(null);
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);

  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    }

    try {
      const saved = JSON.parse(consent);
      const normalized = {
        necessary: true,
        analytics: saved?.analytics === true,
        marketing: saved?.marketing === true,
      };
      setPreferences(normalized);
      if (normalized.analytics) enableAnalytics();
      else disableAnalytics();
    } catch {
      localStorage.removeItem('cookie_consent');
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    const openSettings = () => {
      const consent = localStorage.getItem('cookie_consent');
      if (consent) {
        try {
          const saved = JSON.parse(consent);
          setPreferences({
            necessary: true,
            analytics: saved?.analytics === true,
            marketing: saved?.marketing === true,
          });
        } catch {
          setPreferences({ necessary: true, analytics: false, marketing: false });
        }
      }
      setShowBanner(true);
      setShowSettings(true);
    };
    window.addEventListener('nexus:open-cookie-settings', openSettings);
    return () => window.removeEventListener('nexus:open-cookie-settings', openSettings);
  }, []);

  const closeSettings = useCallback(() => {
    setShowSettings(false);
    // Return focus to the trigger button once the banner re-renders
    requestAnimationFrame(() => settingsBtnRef.current?.focus());
  }, []);

  // Modal a11y: initial focus, focus trap (Tab / Shift+Tab), Escape to close
  useEffect(() => {
    if (!showSettings) return;
    closeBtnRef.current?.focus();
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeSettings();
        return;
      }
      if (e.key !== 'Tab') return;
      const nodes = dialogRef.current?.querySelectorAll(FOCUSABLE);
      if (!nodes || nodes.length === 0) return;
      const list = Array.from(nodes);
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      } else if (!dialogRef.current.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [showSettings, closeSettings]);

  // Background isolation: aria-modal="true" removes background content from the
  // accessibility tree; keyboard isolation is enforced by the focus trap above.

  // Banner keyboard a11y: Escape dismisses the banner (no consent stored).
  useEffect(() => {
    if (!showBanner || showSettings) return;
    const onBannerKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowBanner(false);
      }
    };
    document.addEventListener('keydown', onBannerKeyDown, true);
    return () => document.removeEventListener('keydown', onBannerKeyDown, true);
  }, [showBanner, showSettings]);

  const persist = (values) => {
    localStorage.setItem(
      'cookie_consent',
      JSON.stringify({ ...values, timestamp: new Date().toISOString() })
    );
    if (values.analytics === true) enableAnalytics();
    else disableAnalytics();
    setPreferences(values);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleAcceptAll = () => persist({ necessary: true, analytics: true, marketing: true });
  const handleRejectAll = () => persist({ necessary: true, analytics: false, marketing: false });
  const handleSavePreferences = () => persist(preferences);

  if (!showBanner) return null;

  const focusRing = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/80 focus-visible:ring-offset-1 focus-visible:ring-offset-black';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="fixed bottom-0 left-0 z-[100] px-3 pb-3 pointer-events-none w-full sm:w-auto sm:max-w-sm"
        data-testid="cookie-consent"
      >
        <div className="pointer-events-auto bg-[#060710]/95 backdrop-blur-xl border border-white/10 shadow-2xl">
          <div className="flex items-start gap-3 p-4 sm:p-5">
            <div className="flex-1 min-w-0">
              <div className="text-xs font-mono uppercase tracking-wider text-white/70 mb-2">
                {isTurkish ? 'Çerezler' : 'Cookies'}
              </div>
              <p className="text-xs text-white/70 leading-relaxed">
                {isTurkish
                  ? 'Deneyiminizi iyileştirmek için çerezler kullanıyoruz. Gerekli çerezler zorunludur; analitik ve pazarlama çerezleri için onayınızı istiyoruz.'
                  : 'We use cookies to improve your experience. Necessary cookies are required; we request your consent for analytics and marketing cookies.'}
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-4">
                <button
                  onClick={handleAcceptAll}
                  className={`px-3 py-1.5 bg-white text-black text-xs font-medium hover:bg-white/90 transition-colors ${focusRing}`}
                  data-testid="cookie-accept-all"
                >
                  {isTurkish ? 'Tümünü Kabul Et' : 'Accept All'}
                </button>
                <button
                  onClick={handleRejectAll}
                  className={`px-3 py-1.5 border border-white/20 text-white/80 text-xs font-medium hover:bg-white/5 hover:border-white/30 transition-colors ${focusRing}`}
                  data-testid="cookie-reject-all"
                >
                  {isTurkish ? 'Sadece Gerekli' : 'Necessary Only'}
                </button>
                <button
                  ref={settingsBtnRef}
                  onClick={() => setShowSettings(true)}
                  aria-haspopup="dialog"
                  aria-expanded={showSettings}
                  className={`px-2 py-1.5 text-[10px] font-mono uppercase tracking-wider text-white/50 hover:text-white transition-colors ${focusRing}`}
                  data-testid="cookie-settings"
                >
                  {isTurkish ? 'Ayarlar' : 'Settings'}
                </button>
              </div>
            </div>
            <button
              onClick={handleRejectAll}
              className={`text-white/40 hover:text-white p-1 transition-colors ${focusRing}`}
              aria-label={isTurkish ? 'Çerez bildirimini kapat' : 'Close cookie notice'}
              data-testid="cookie-close"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Accessible Cookie Settings modal */}
        {showSettings && (
          <div className="pointer-events-auto fixed inset-0 z-[110] flex items-center justify-center px-4">
            <div
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={closeSettings}
              aria-hidden="true"
              data-testid="cookie-settings-overlay"
            />
            <div
              ref={dialogRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="cookie-settings-title"
              className="relative w-full max-w-md bg-[#060710] border border-white/15 shadow-2xl p-5 sm:p-6"
              data-testid="cookie-settings-dialog"
            >
              <div className="flex items-center justify-between mb-5">
                <h2 id="cookie-settings-title" className="text-xs font-mono uppercase tracking-wider text-white/60">
                  {isTurkish ? 'Çerez Ayarları' : 'Cookie Settings'}
                </h2>
                <button
                  ref={closeBtnRef}
                  onClick={closeSettings}
                  aria-label={isTurkish ? 'Çerez ayarlarını kapat' : 'Close cookie settings'}
                  className={`text-white/40 hover:text-white p-1 transition-colors ${focusRing}`}
                  data-testid="cookie-settings-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-3 mb-5">
                {[
                  {
                    key: 'necessary',
                    title: isTurkish ? 'Gerekli' : 'Necessary',
                    desc: isTurkish ? 'Sitenin çalışması için zorunlu' : 'Required for site functionality',
                    locked: true,
                  },
                  {
                    key: 'analytics',
                    title: isTurkish ? 'Analitik' : 'Analytics',
                    desc: isTurkish ? 'Site kullanımını analiz eder' : 'Analyzes site usage',
                  },
                  {
                    key: 'marketing',
                    title: isTurkish ? 'Pazarlama' : 'Marketing',
                    desc: isTurkish ? 'Kişiselleştirilmiş reklamlar' : 'Personalized advertising',
                  },
                ].map((row) => {
                  const active = row.locked ? true : preferences[row.key];
                  return (
                    <div
                      key={row.key}
                      className="flex items-center justify-between gap-4 py-3 border-b border-white/10"
                    >
                      <div>
                        <div className="text-sm text-white">{row.title}</div>
                        <div className="text-xs text-white/40">{row.desc}</div>
                      </div>
                      <button
                        disabled={row.locked}
                        onClick={() =>
                          !row.locked &&
                          setPreferences({ ...preferences, [row.key]: !preferences[row.key] })
                        }
                        role="switch"
                        aria-checked={active}
                        className={`relative w-10 h-5 border transition-colors ${focusRing} ${
                          active
                            ? 'bg-white border-white'
                            : 'bg-transparent border-white/30'
                        } ${row.locked ? 'opacity-50 cursor-not-allowed' : ''}`}
                        aria-label={row.title}
                        data-testid={`cookie-toggle-${row.key}`}
                      >
                        <span
                          className={`absolute top-0.5 w-4 h-4 transition-all ${
                            active
                              ? 'right-0.5 bg-black'
                              : 'left-0.5 bg-white/70'
                          }`}
                        />
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleSavePreferences}
                  className={`px-4 py-2 bg-white text-black text-sm font-medium hover:bg-white/90 transition-colors ${focusRing}`}
                  data-testid="cookie-save-preferences"
                >
                  {isTurkish ? 'Kaydet' : 'Save Preferences'}
                </button>
                <button
                  onClick={handleAcceptAll}
                  className={`px-4 py-2 border border-white/20 text-white/80 text-sm font-medium hover:bg-white/5 hover:border-white/30 transition-colors ${focusRing}`}
                  data-testid="cookie-settings-accept-all"
                >
                  {isTurkish ? 'Tümünü Kabul Et' : 'Accept All'}
                </button>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
